import { useQuery } from '@tanstack/react-query'
import { activityApi, type ManagerActivity } from '../api/activity'
import { Spinner } from '../components/Spinner'

// 'YYYY-MM-DD' → 'DD/MM'
function ddmm(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

// Há quantos dias foi a última atividade (0 = hoje). null = nunca.
function daysSinceLast(activeByDay: boolean[]): number | null {
  const last = activeByDay.lastIndexOf(true)
  if (last === -1) return null
  return activeByDay.length - 1 - last
}

function lastActivityBadge(daysAgo: number | null) {
  if (daysAgo === null) return { label: 'Nunca', cls: 'bg-red-100 text-red-700' }
  if (daysAgo === 0) return { label: 'Hoje', cls: 'bg-emerald-100 text-emerald-700' }
  if (daysAgo === 1) return { label: 'Ontem', cls: 'bg-emerald-100 text-emerald-700' }
  if (daysAgo <= 6) return { label: `Há ${daysAgo} dias`, cls: 'bg-amber-100 text-amber-700' }
  return { label: `Há ${daysAgo} dias`, cls: 'bg-red-100 text-red-700' }
}

export default function ManagerActivity() {
  const { data, isLoading } = useQuery({
    queryKey: ['activity-managers'],
    queryFn: () => activityApi.managers(),
  })

  const days = data?.days ?? []
  const managers = data?.managers ?? []

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Atividade dos gerentes</h1>
        <p className="mt-1 text-sm text-slate-500">
          Últimos {days.length || 30} dias (horário de São Paulo). Um dia é "ativo" quando o gerente
          fez ao menos uma ação em relatório, enviou achados de investigação, marcou um relatório
          como lido ou gerou um relatório.
        </p>
      </div>

      {isLoading ? (
        <Spinner />
      ) : managers.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum gerente ativo.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Gerente</th>
                <th className="px-4 py-2.5">Última atividade</th>
                <th className="px-4 py-2.5">Dias ativos</th>
                <th className="px-4 py-2.5">Últimos {days.length} dias →</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {managers.map((m) => (
                <Row key={m.id} m={m} days={days} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-emerald-500" /> ativo
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-slate-200" /> sem atividade
        </span>
      </div>
    </div>
  )
}

function Row({ m, days }: { m: ManagerActivity; days: string[] }) {
  const daysAgo = daysSinceLast(m.activeByDay)
  const badge = lastActivityBadge(daysAgo)
  return (
    <tr>
      <td className="px-4 py-2.5">
        <div className="font-medium text-slate-900">{m.name}</div>
        <div className="text-xs text-slate-400">{m.email}</div>
      </td>
      <td className="px-4 py-2.5">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.cls}`}>
          {badge.label}
        </span>
      </td>
      <td className="px-4 py-2.5 text-slate-600">
        {m.activeDaysCount}/{days.length}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap gap-0.5">
          {m.activeByDay.map((active, i) => (
            <span
              key={i}
              title={`${days[i]}${active ? ' · ativo' : ' · sem atividade'}`}
              className={`h-3 w-3 rounded-sm ${active ? 'bg-emerald-500' : 'bg-slate-200'} ${
                i === days.length - 1 ? 'ring-1 ring-slate-400' : ''
              }`}
            />
          ))}
        </div>
      </td>
    </tr>
  )
}
